import { Box, Button, Paper, Stack, Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useTranslation } from 'react-i18next'
import { IoIosArrowBack } from 'react-icons/io'
import { useLocation, useNavigate } from 'react-router-dom'

import useClientData from '../../hooks/useClientData'
import useConfiguration from '../../hooks/useConfiguration'
import useLogin from '../../hooks/useLogin'
import { ISendLoginPayload } from '../../redux/login'
import getQueryParams from '../../utils/getQueryParams'
import HomeSkeleton from '../Home/HomeSkeleton'
import { getResetPasswordURL } from './helpers'
import LoginForm, { LoginInputs } from './LoginForm'
import LoginSkeleton from './LoginSkeleton'
import { ExternalLink, HeaderText, LoadingIcon, MessageContainer, SubmitButton, Subtitle } from './styles'

const Login = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  const { configuration } = useConfiguration()
  const { sendLogin, resetLogin, loginCompleted, error, socialToken, isLoading } = useLogin()
  const { clientData, isLoading: clientDataIsLoading } = useClientData()

  const { userId, sessionToken } = getQueryParams({ url: location.search })

  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const [socialLoginFailed, wrongCredentials] = useMemo(() => {
    recaptchaRef.current?.reset()
    const noSocialLogin = error && error.status === 400
    const notFoundError = error && error.status === 404
    return [noSocialLogin, notFoundError]
  }, [error, recaptchaRef])

  const warningMessage = useMemo(() => {
    let message = ''
    if (socialLoginFailed) {
      message = t('login.warnings.socialLoginFailed')
    }
    if (wrongCredentials) {
      message = t('login.warnings.wrongLoginPassword')
    }
    return message
  }, [socialLoginFailed, wrongCredentials])

  const resetPasswordURL = getResetPasswordURL({
    userId,
    sessionToken,
    url: configuration?.extensions.resetPasswordURL,
    provider: configuration?.webview.provider === 'wa' ? 'whatsapp' : 'messenger',
  })

  const handleBackButton = useCallback(() => {
    resetLogin()
    navigate(-1)
  }, [])

  const onFormSubmit = useCallback(
    async ({ cpf, password }: LoginInputs) => {
      const recaptchaCode = await recaptchaRef.current?.executeAsync()

      const payload: ISendLoginPayload = {
        type: 'simple',
        username: cpf,
        password,
        userId,
        recaptchaCode: recaptchaCode || '',
        middleware: configuration?.extensions.sendLoginMiddleware,
      }

      if (socialLoginFailed && socialToken) {
        payload.socialToken = socialToken
      }

      sendLogin(payload)
    },
    [configuration, recaptchaRef, socialLoginFailed, socialToken, userId]
  )

  const handleRecaptchaExpired = useCallback(() => {
    recaptchaRef.current?.reset()
  }, [recaptchaRef])

  useEffect(() => {
    if (loginCompleted && configuration) {
      navigate(`/${configuration.slug}/redirect${location.search}`, { state: location.pathname })
    }
  }, [loginCompleted, location, navigate, configuration])

  if (!configuration) {
    return <HomeSkeleton />
  }

  return (
    <Stack direction="column" p={4} height="100%">
      <Box display="flex" justifyContent="center" marginBottom={5}>
        <Button
          onClick={handleBackButton}
          style={{
            ...configuration.extensions.webviewCustomizations?.unstyledButton,
            position: 'absolute',
            left: '4px',
            top: '20px',
          }}
        >
          <IoIosArrowBack size={30} />
        </Button>

        <Paper elevation={0} style={configuration.extensions.webviewCustomizations?.logo}>
          <img
            loading="lazy"
            width="100%"
            height="100%"
            alt={`${configuration.name} logo`}
            src={configuration.webview.theme.logo}
          />
        </Paper>
      </Box>

      <HeaderText gutterBottom variant="h5">
        {t('login.clientLogin')}
      </HeaderText>

      <Subtitle variant="subtitle1">{`${t('login.enterPassword')} ${configuration.name}.`}</Subtitle>

      <MessageContainer error={!!wrongCredentials} message={!!socialLoginFailed}>
        <Typography
          color="inherit"
          variant="subtitle2"
          lineHeight={1.2}
          dangerouslySetInnerHTML={{
            __html: warningMessage,
          }}
        />
      </MessageContainer>

      {!clientData || clientDataIsLoading ? (
        <LoginSkeleton />
      ) : (
        <LoginForm focusColor={configuration.webview.theme.customerColor} onSubmitCallback={onFormSubmit}>
          <ExternalLink target="_blank" href={resetPasswordURL} rel="noreferrer">
            {t('button.forgotPassword')}
          </ExternalLink>

          <SubmitButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            disabled={isLoading}
            style={configuration.extensions.webviewCustomizations?.primaryButton}
          >
            {t('button.enter')}
            {isLoading ? <LoadingIcon size={20} /> : ''}
          </SubmitButton>
        </LoginForm>
      )}

      <ReCAPTCHA
        size="invisible"
        ref={recaptchaRef}
        onExpired={handleRecaptchaExpired}
        sitekey={configuration.extensions.recaptchaSiteKey}
        badge="bottomleft"
      />
    </Stack>
  )
}

export default Login
